/**
 * 同一ページ内のハッシュリンクを、固定ヘッダー分を差し引いてスクロールする。
 * - `#id` / `/#id` / `https://.../#id` を対象
 * - 別ページからのハッシュ着地は load 後に複数回補正（画像・親JSのレイアウト後）
 */
export default class SmoothScroll {
  constructor() {
    this.smoothScrollTrigger = [...document.querySelectorAll('a[href*="#"]')].filter((el) => {
      const href = el.getAttribute('href');
      if (!href || href === '#' || href.trim() === '') return false;
      return Boolean(this.getHashId(href));
    });

    this.init();
  }

  getHashId(href) {
    const index = href.indexOf('#');
    if (index === -1) return '';
    return decodeURIComponent(href.slice(index + 1).split('?')[0].split('&')[0]);
  }

  isSamePageLink(href) {
    if (href.startsWith('#')) return true;
    if (href.startsWith('/#')) return true;

    try {
      const url = new URL(href, window.location.href);
      const current = new URL(window.location.href);
      const normalize = (path) => path.replace(/\/$/, '') || '/';
      return (
        url.origin === current.origin &&
        normalize(url.pathname) === normalize(current.pathname)
      );
    } catch (error) {
      return false;
    }
  }

  /** サブメニュー開閉用の親リンクは親テーマに任せる */
  isSubmenuParentLink(el) {
    const parentItem = el.closest('.ol-header__nav-list > .menu-item-has-children');
    if (!parentItem) return false;
    const parentAnchor = parentItem.querySelector(':scope > .ol-header__nav-anchor');
    return parentAnchor === el;
  }

  /** 親テーマ common.js と同様に SP メニューを閉じる */
  closeMobileMenu() {
    const menuToggle = document.querySelector('.ol-hamburger__menu-icon');
    const headerInner = document.querySelector('.ol-header__inner');
    const body = document.body;
    if (!menuToggle || !menuToggle.classList.contains('is-open')) return;

    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    body.classList.remove('js-body__noscroll');
    menuToggle.classList.remove('is-open');
    menuToggle.classList.add('is-close');

    if (headerInner) {
      headerInner.classList.remove('is-open');
      headerInner.classList.add('is-close');
      if (scrollPosition === 0) {
        headerInner.classList.remove('js-header-scroll');
      }
    }
  }

  /**
   * @param {string} targetId
   * @param {ScrollBehavior} [behavior='smooth']
   */
  scrollToTarget(targetId, behavior = 'smooth') {
    const targetElement = document.getElementById(targetId);
    if (!targetElement) return;

    const gap = this.getHeaderGap();
    const top =
      targetElement.getBoundingClientRect().top + window.scrollY - gap;

    window.scrollTo({
      top: Math.max(0, top),
      behavior,
    });
  }

  getHeaderGap() {
    const header =
      document.getElementById('olHeader') ||
      document.querySelector('.ol-header') ||
      document.getElementById('js-header');

    if (!header) return 0;

    const rect = header.getBoundingClientRect();
    if (rect.height <= 0) return 0;

    // メニュー展開中の巨大な高さは使わず、通常ヘッダー相当に抑える
    const compact = header.querySelector('.ol-header__title-anchor, .ol-header__inner');
    if (compact) {
      const compactBottom = compact.getBoundingClientRect().bottom;
      if (compactBottom > 0 && compactBottom < window.innerHeight * 0.4) {
        return compactBottom;
      }
    }

    return Math.min(Math.max(rect.bottom, rect.height), 120);
  }

  /** 別ページからのハッシュ着地用（レイアウト確定後に何度か合わせる） */
  scrollToHashOnLanding() {
    const hash = this.getHashId(window.location.hash || '');
    if (!hash || !document.getElementById(hash)) return;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const run = () => this.scrollToTarget(hash, 'auto');

    // ブラウザ標準ジャンプの直後・load 後・親テーマの高さ調整後に再補正
    requestAnimationFrame(run);
    window.addEventListener('load', () => {
      run();
      [100, 300, 600, 1000].forEach((delay) => {
        window.setTimeout(run, delay);
      });
    });
  }

  bindClickHandlers() {
    this.smoothScrollTrigger.forEach((el) => {
      el.addEventListener(
        'click',
        (e) => {
          if (this.isSubmenuParentLink(el)) return;

          const href = el.getAttribute('href');
          if (!href || !this.isSamePageLink(href)) return;

          const targetId = this.getHashId(href);
          if (!targetId || !document.getElementById(targetId)) return;

          e.preventDefault();
          e.stopImmediatePropagation();

          this.closeMobileMenu();

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.scrollToTarget(targetId, 'smooth');
              if (history.replaceState) {
                history.replaceState(null, '', `#${targetId}`);
              }
            });
          });
        },
        true
      );
    });
  }

  init() {
    if (this.smoothScrollTrigger.length !== 0) {
      this.bindClickHandlers();
    }
    this.scrollToHashOnLanding();
  }
}
